import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { http } from "@/lib/api";
import LeadQuickAdd from "@/components/LeadQuickAdd";
import { STATUS_META, TIER_META } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Search } from "lucide-react";

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [ref, setRef] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [tier, setTier] = useState("all");
  const [country, setCountry] = useState("all");
  const [openAdd, setOpenAdd] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    const params = {};
    if (q) params.q = q;
    if (status !== "all") params.status = status;
    if (tier !== "all") params.tier = tier;
    if (country !== "all") params.country = country;
    const { data } = await http.get("/leads", { params });
    setLeads(data);
  };

  useEffect(() => {
    http.get("/reference").then((r) => setRef(r.data));
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, tier, country]);

  return (
    <Layout title="Leads"
            subtitle={`${leads.length} lead${leads.length === 1 ? "" : "s"}`}
            actions={
              <Button data-testid="btn-add-lead" onClick={() => setOpenAdd(true)}
                      className="bg-amber-600 hover:bg-amber-700 text-white h-10">
                <Plus className="w-4 h-4 mr-1.5" /> Quick-Add Lead
              </Button>
            }>
      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="leads-search"
            placeholder="Search company or city…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            className="pl-9 h-10 border-slate-300"
          />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[160px] h-10" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {ref?.lead_statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={tier} onValueChange={setTier}>
          <SelectTrigger className="w-[140px] h-10" data-testid="filter-tier"><SelectValue placeholder="Tier" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All tiers</SelectItem>
            <SelectItem value="Tier 1">Tier 1</SelectItem>
            <SelectItem value="Tier 2">Tier 2</SelectItem>
            <SelectItem value="Tier 3">Tier 3</SelectItem>
          </SelectContent>
        </Select>
        <Select value={country} onValueChange={setCountry}>
          <SelectTrigger className="w-[180px] h-10" data-testid="filter-country"><SelectValue placeholder="Country" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All countries</SelectItem>
            {ref?.all_countries.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={load} data-testid="btn-apply-filters" className="h-10">Apply</Button>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr className="text-[11px] uppercase tracking-wider text-slate-500">
                <th className="text-left px-4 py-3 font-semibold">Company</th>
                <th className="text-left px-4 py-3 font-semibold">Country</th>
                <th className="text-left px-4 py-3 font-semibold">Industry</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-left px-4 py-3 font-semibold">Tier</th>
                <th className="text-right px-4 py-3 font-semibold">Score</th>
                <th className="text-left px-4 py-3 font-semibold">Added</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {leads.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-sm text-slate-500" data-testid="leads-empty">
                  No leads yet — click <span className="font-semibold text-slate-700">Quick-Add Lead</span> to create your first one.
                </td></tr>
              )}
              {leads.map((l) => {
                const st = STATUS_META[l.status] || STATUS_META["New"];
                const tm = TIER_META[l.tier];
                return (
                  <tr key={l.id} onClick={() => nav(`/leads/${l.id}`)}
                      className="hover:bg-amber-50/40 cursor-pointer transition-colors"
                      data-testid={`lead-row-${l.id}`}>
                    <td className="px-4 py-3 text-sm">
                      <div className="font-medium text-slate-900">{l.company_name}</div>
                      <div className="text-xs text-slate-500 truncate max-w-[280px]">{l.one_line_reason}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700">{l.country}</td>
                    <td className="px-4 py-3 text-sm text-slate-700">{l.industry}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${st.className}`}>
                        {l.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {tm ? (
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${tm.className}`}>
                          <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${tm.dot}`}></span>{tm.label}
                        </span>
                      ) : <span className="text-slate-300 text-xs">—</span>}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-sm font-semibold text-slate-900">
                      {l.weighted_score != null ? l.weighted_score.toFixed(1) :
                        <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      {new Date(l.date_added).toLocaleDateString()}<br />
                      <span className="text-slate-400">by {l.added_by_name}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {openAdd && ref && (
        <LeadQuickAdd
          ref_data={ref}
          onClose={() => setOpenAdd(false)}
          onCreated={(lead) => { setOpenAdd(false); nav(`/leads/${lead.id}`); }}
        />
      )}
    </Layout>
  );
}
